/**
 * 客户端拆字分析
 * 输入：中文名字
 * 输出：字义、部首、笔画、主题词
 */

import radicalsData from "../data/radicals.json";
import charsData from "../data/chars.json";
import radicalMapData from "../data/radicalToOrgan.json";
import { determineElements, analyzeElements } from "./elements";
import { judgeFortune } from "./fortune";
import { lookupFamous, FAME_BUFF } from "./data/famousPeople";
import type { CharAnalysis, CharInfo, FameBuff } from "../shared/types";

interface RadicalsDB {
  radicals: Record<string, {
    name: string;
    stroke: number;
    category: string;
    alias?: string[];
  }>;
}

interface CharsDB {
  chars: Record<string, {
    pinyin: string;
    structure: string;
    strokes: number;
    meanings: string[];
  }>;
}

interface RadicalMapDB {
  radicalMap: Record<string, string>;
  defaultMap: string;
}

const radicals = (radicalsData as RadicalsDB).radicals;
const chars = (charsData as CharsDB).chars;
const radicalMap = (radicalMapData as RadicalMapDB).radicalMap;
const radicalMapValues = new Set(Object.values(radicalMap));

const RADICAL_KEYS = new Set(Object.keys(radicals));

const CHAR_RADICALS: Record<string, string[]> = {
  "王": ["王"], "李": ["木"], "张": ["弓"], "刘": ["刂"], "陈": ["阝"],
  "杨": ["木"], "黄": ["黄"], "赵": ["走"], "周": ["口"], "吴": ["口"],
  "徐": ["彳"], "孙": ["子"], "朱": ["木"], "马": ["马"], "胡": ["月"],
  "郭": ["阝"], "林": ["木"], "何": ["亻"], "高": ["高"], "铁": ["钅"],
  "柱": ["木"], "明": ["日"], "伟": ["亻"], "芳": ["艹"], "娜": ["女"],
  "敏": ["攵"], "静": ["青"], "丽": ["一"], "强": ["弓"], "磊": ["石"],
  "军": ["冖"], "洋": ["氵"], "勇": ["力"], "艳": ["丰"], "杰": ["木"],
  "娟": ["女"], "涛": ["氵"], "超": ["走"], "秀": ["禾"], "英": ["艹"],
  "平": ["干"], "刚": ["冈"], "桂": ["木"], "文": ["文"], "华": ["十"],
  "建": ["廴"], "斌": ["文"], "婷": ["女"], "慧": ["心"], "欣": ["斤"],
  "浩": ["氵"], "然": ["灬"], "子": ["子"], "轩": ["车"], "梓": ["木"],
  "萱": ["艹"], "雨": ["雨"], "诗": ["讠"], "梦": ["夕"], "思": ["心"],
  "嘉": ["加"], "瑞": ["王"], "天": ["大"], "满": ["氵"], "婉": ["女"],
  "清": ["氵"], "阿": ["阝"], "牛": ["牛"], "逍": ["辶"], "遥": ["辶"],
  "仑": ["人"], "玉": ["王"], "龙": ["龙"], "宇": ["宀"], "宙": ["宀"],
  "涵": ["氵"], "冰": ["冫"], "雪": ["雨"], "月": ["月"], "光": ["儿"],
  "星": ["日"], "辰": ["辰"], "夕": ["夕"], "云": ["二"], "风": ["风"],
  "雷": ["雨"], "电": ["电"], "火": ["火"], "水": ["水"], "木": ["木"],
  "土": ["土"], "金": ["金"], "山": ["山"], "石": ["石"], "田": ["田"],
  "人": ["人"], "心": ["心"], "手": ["手"], "口": ["口"], "目": ["目"],
  "耳": ["耳"], "足": ["足"], "女": ["女"], "日": ["日"], "鬼": ["鬼"],
  "神": ["礻"], "魔": ["麻"], "妖": ["女"], "兽": ["犬"], "虫": ["虫"],
  "鸟": ["鸟"], "鱼": ["鱼"], "花": ["艹"], "草": ["艹"], "树": ["木"],
  "根": ["木"], "叶": ["艹"], "红": ["纟"], "白": ["白"], "黑": ["黑"],
  "紫": ["糸"], "绿": ["纟"], "蓝": ["纟"], "彩": ["彡"], "无": ["无"],
  "名": ["口"], "字": ["宀"], "一": ["一"], "小": ["小"], "三": ["一"],
};

const CHAR_TAGS: Record<string, string[]> = {
  "王": ["王权", "统治", "三横一竖", "姓氏"],
  "铁": ["金属", "坚硬", "重", "黑"],
  "柱": ["支撑", "竖立", "木质", "中心"],
  "林": ["森林", "树木", "双木"],
  "明": ["光亮", "日月", "智慧"],
  "雪": ["寒冷", "白色", "飘落"],
  "冰": ["寒冷", "坚硬", "晶体"],
  "水": ["流体", "透明", "滋养"],
  "火": ["炽热", "光明", "破坏"],
  "风": ["流动", "无形", "自由"],
  "雨": ["流动", "云层", "滋养"],
  "雷": ["巨响", "电光", "天威"],
  "龙": ["神话", "威严", "鳞片", "长身"],
  "魔": ["黑暗", "异常", "无形"],
  "鬼": ["幽灵", "阴冷", "半透明"],
  "神": ["光环", "神圣", "威能"],
  "星": ["光点", "夜空", "遥远"],
  "月": ["柔和", "周期", "银色"],
  "日": ["炽热", "圆形", "光芒"],
  "花": ["鲜艳", "芬芳", "绽放"],
  "草": ["柔软", "绿色", "低矮"],
  "树": ["高大", "木质", "年轮"],
  "虫": ["多足", "蠕动", "小"],
  "鸟": ["羽毛", "飞翔", "喙"],
  "鱼": ["鳞片", "水生", "鳍"],
  "兽": ["毛皮", "利爪", "野性"],
  "石": ["坚硬", "沉重", "灰色"],
  "山": ["高大", "陡峭", "稳定"],
  "土": ["厚重", "基础", "黄"],
  "金": ["金属", "光泽", "重"],
  "玉": ["温润", "半透", "白绿"],
  "云": ["飘逸", "白", "不定形"],
  "梦": ["不定形", "紫", "模糊"],
  "思": ["脑部", "无形", "持续"],
};

const ORGAN_NAMES: Record<string, string> = {
  "metal_scale": "金属鳞",
  "wood_shell": "树皮壳",
  "fluid_body": "流体身",
  "flame_aura": "火焰光环",
  "earth_skin": "土质皮肤",
  "stone_armor": "岩石甲",
  "mountain_back": "山型背",
  "flesh_pulse": "血肉肌",
  "heart_organ": "心脏",
  "multi_eye": "多眼",
  "long_ear": "长耳",
  "many_mouth": "多嘴",
  "insect_leg": "虫足",
  "wing": "翅膀",
  "dragon_horn": "龙角",
  "ghost_aura": "幽灵雾",
  "halo": "光环",
  "humanoid_body": "人形身",
  "soft_limb": "柔肢",
  "beast_leg": "兽足",
  "mist_body": "雾身",
  "flowing_form": "流动形",
  "crown": "王冠",
  "leather_wing": "革翼",
  "thread_tendril": "丝触手",
  "bright_eye": "亮眼",
  "dark_eye": "暗眼",
  "tear_streak": "泪痕",
  "golden_horn": "金角",
  "blood_vein": "血纹",
  "leaf_fin": "叶鳍",
  "abstract_blob": "抽象核",
};

function detectRadical(char: string): string[] {
  return CHAR_RADICALS[char] ?? [];
}

function extractCharTags(char: string, meanings: string[]): string[] {
  const tags: string[] = [];
  const known = CHAR_TAGS[char];
  if (known) tags.push(...known);
  if (meanings.some((m) => m.includes("surname"))) tags.push("姓氏");
  return tags;
}

export function analyze(name: string): CharAnalysis {
  const trimmed = name.trim();
  const charsArr: CharInfo[] = [];
  const themes: string[] = [];
  const radicalSummary: string[] = [];
  // 强制下品开关（非姓名规范输入 → 五等）
  const forceLowest = !isNameLike(trimmed);

  for (const c of trimmed) {
    const cd = chars[c];
    if (cd) {
      const rads = detectRadical(c);
      const tags = extractCharTags(c, cd.meanings);
      charsArr.push({
        char: c,
        pinyin: cd.pinyin,
        radicals: rads,
        strokes: cd.strokes,
        structure: cd.structure,
        meanings: cd.meanings,
      });
      for (const t of tags) {
        if (!themes.includes(t)) themes.push(t);
      }
      for (const r of rads) {
        const organ = radicalMap[r] ?? "abstract_blob";
        const organName = ORGAN_NAMES[organ] ?? organ;
        radicalSummary.push(`${r}→${organName}`);
      }
    } else {
      charsArr.push({
        char: c,
        pinyin: "?",
        radicals: [],
        strokes: 0,
        structure: "未知",
        meanings: ["未知字符"],
      });
    }
  }

  // 五行判定
  const strokes = charsArr.map((c) => c.strokes);
  const { dominant, counts, missing } = determineElements(trimmed, strokes);
  const elementAnalysis = analyzeElements(trimmed, strokes);
  // 阶层判定（按《五行大义》五等阶位）
  let fortune = judgeFortune(trimmed, counts, dominant, strokes);
  // 非姓名规范输入 → 强制五等 / 下品 / 极低分
  if (forceLowest) {
    fortune = {
      ...fortune,
      total: 0,
      rank: "下品",
      title: "孤渊未开",
      nayin: "无",
      verse: "此名不合规范，五行闭塞，气运未启",
      breakdown: {
        virtue: { score: 0, note: "非姓名规范输入，五行无根" },
        pitch: { score: 0, note: "音阶无效" },
        sanCai: { score: 0, note: "三才不通" },
        penalty: { score: 0, note: "名字不合规" },
      },
    };
  }

  // 名人 buff 检测（非姓名规范输入不触发）
  const fame = forceLowest
    ? {
        hit: false,
        tier: null,
        displayName: "",
        shortChar: "",
        title: "",
        field: "",
        era: "",
        scoreBonus: 0,
        effectiveTotal: fortune.total,
        effectiveRank: "五等" as const,
      }
    : detectFame(trimmed, fortune.total, fortune.rank);

  return {
    raw: trimmed,
    chars: charsArr,
    themes: themes.slice(0, 8),
    radicalSummary: radicalSummary.slice(0, 6),
    dominantElement: dominant,
    elementCounts: counts,
    missingElements: missing,
    elementAnalysis,
    fortune,
    fame,
  };
}

/** 根据 fortune + 名人命中计算 FameBuff
 *  同一名字的 fortuneTotal + 词库分数 = 固定 effectiveTotal
 *  同一名字的 effectiveRank = 固定（完全确定性，无随机）
 */
function detectFame(rawName: string, fortuneTotal: number, fortuneRank: string): FameBuff {
  const person = lookupFamous(rawName);
  if (!person) {
    return {
      hit: false,
      tier: null,
      displayName: "",
      shortChar: "",
      title: "",
      field: "",
      era: "",
      scoreBonus: 0,
      effectiveTotal: fortuneTotal,
      effectiveRank: fortuneRankToRankLevel(fortuneRank),
    };
  }
  const buff = FAME_BUFF[person.tier];
  // 总加成 = 知名度 tier 加分 + 历史贡献分（可正可负）
  const historyScore = person.historyScore ?? 0;
  const totalBonus = buff.scoreBonus + historyScore;
  // 范围 -60 ~ +40（user 规则）
  const clampedBonus = Math.max(-60, Math.min(40, totalBonus));
  const effectiveTotal = Math.min(100, Math.round(fortuneTotal + clampedBonus));
  const effectiveRank = fortuneRankToRankLevel(fortuneRank);

  // 选 shortChar：取人名除姓之外的首字（多字人名取第二字）
  let shortChar = "";
  if (person.name.length >= 2) shortChar = person.name[1];
  else shortChar = person.name[0];

  return {
    hit: true,
    tier: person.tier,
    displayName: person.name,
    shortChar,
    title: person.title,
    field: person.field,
    era: person.era,
    scoreBonus: clampedBonus,
    effectiveTotal,
    effectiveRank,
    contribution: person.contribution,
    historyScore,
  };
}

/** "上品" → "一等" 映射 */
function fortuneRankToRankLevel(rank: string): FameBuff["effectiveRank"] {
  switch (rank) {
    case "上品": return "一等";
    case "中上品": return "二等";
    case "中品": return "三等";
    case "中下品": return "四等";
    case "下品":
    default: return "五等";
  }
}

function rankLevelToNumber(r: FameBuff["effectiveRank"]): number {
  return { "一等": 1, "二等": 2, "三等": 3, "四等": 4, "五等": 5 }[r];
}
function numberToRankLevel(n: number): FameBuff["effectiveRank"] {
  if (n <= 1) return "一等";
  if (n <= 2) return "二等";
  if (n <= 3) return "三等";
  if (n <= 4) return "四等";
  return "五等";
}

/**
 * 校验规则（用户放宽后）：
 *  - 仅当输入 ≤ 6 个汉字时归类为名字
 *  - 不设任何字的黑名单（不拦截"小字"、"大字"等正常用字）
 *  - 仍然拒绝：
 *    - 长度 > 6 的字符串（多半是短语 / 句子）
 *    - 非汉字字符（标点 / 字母 / 数字 / emoji）
 *
 * 备注：用户反馈有"正常用字被禁用"的情况，特意放宽黑名单。
 *  长度阈值与汉字校验已足以区分名字和短语 / 句子。
 */
const NAME_PUNCT = /[，。！？、；：""''「」『』（）()【】\[\]·…—\-\.,!?;:'"()\[\]·]/;

export function isValidName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  // 长度限制：1 ~ 6 字
  if (trimmed.length < 1 || trimmed.length > 6) return false;
  // 全字符必须都是汉字（含生僻字、扩展 CJK 字符）
  for (const c of trimmed) {
    // 放宽到 CJK 统一汉字扩展（包含生僻字，如「龘」「彝」等）
    if (!/[\u4e00-\u9fff\u3400-\u4dbf]/.test(c)) return false;
  }
  // 拒绝标点符号
  if (NAME_PUNCT.test(trimmed)) return false;
  return true;
}

export function isNameLike(name: string): boolean {
  return isValidName(name);
}
